/**
 * Settings → Etiquetas — the tag vocabulary.
 *
 * This screen owns the words, not who wears them. Attaching `dominio:android`
 * to a memory happens in the memory's own editor, because that is an edit to
 * the memory; here a person only decides which words exist, what they mean and
 * which ones are retired.
 *
 * ── Why the list is grouped by namespace ──────────────────────────────────
 *
 * Because the namespace *is* the mechanism. Matching is an intersection within
 * a namespace and a conjunction across them, so a flat alphabetical list would
 * teach precisely the wrong model: that tags are a bag of labels where any
 * match will do. Grouped, the screen reads the way the rule works — each group
 * is one question a tagged thing may choose to answer.
 *
 * Follows MemoriesSection's pattern: manual `refresh()` + useState, ModalShell
 * for the editor, `window.confirm` before retiring.
 */
import type { AdminTagListEntry, Tag, TagId } from "@taskin-code/contracts";
import { TAG_DESCRIPTION_MAX_LENGTH, formatTagRef } from "@taskin-code/contracts";
import { useEffect, useState } from "react";

import { useAuthStore } from "../../auth/authStore";
import { TagsClient } from "../../tags/TagsClient";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Field, ModalShell } from "./settingsModal";
import {
  buildTagCreateRequest,
  buildTagUpdateRequest,
  EMPTY_TAG_FORM,
  groupTagsByNamespace,
  handleFromLabel,
  tagToFormState,
  validateTagForm,
  type TagFormState,
} from "./TagsSection.logic";

/** The chip as it appears everywhere else, so the editor previews the truth. */
function TagChip({ tag, muted }: { readonly tag: Tag; readonly muted?: boolean }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
        muted ? "bg-muted text-muted-foreground" : "bg-muted/60 text-foreground"
      }`}
    >
      {tag.color !== null && (
        <span aria-hidden className="size-2 rounded-full" style={{ backgroundColor: tag.color }} />
      )}
      {tag.label}
    </span>
  );
}

function TagEditor(props: {
  readonly initial: TagFormState;
  readonly isEdit: boolean;
  readonly onClose: () => void;
  readonly onSave: (form: TagFormState) => Promise<void>;
}) {
  const [form, setForm] = useState<TagFormState>(props.initial);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const invalid = validateTagForm(form);
    if (invalid !== null) {
      setError(invalid);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await props.onSave(form);
      props.onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar la etiqueta.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title={props.isEdit ? "Editar etiqueta" : "Nueva etiqueta"} onClose={props.onClose}>
      <div className="flex flex-col gap-4">
        <Field label="Nombre">
          <Input
            value={form.label}
            onChange={(e) => {
              const label = e.target.value;
              // The handle follows the name only while creating: on an edit it
              // is identity, and the server refuses to patch it.
              setForm((prev) =>
                props.isEdit
                  ? { ...prev, label }
                  : { ...prev, label, slug: handleFromLabel(label) },
              );
            }}
            placeholder="Android"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Namespace">
            <Input
              value={form.namespace}
              disabled={props.isEdit}
              onChange={(e) => setForm((prev) => ({ ...prev, namespace: e.target.value }))}
              placeholder="dominio"
            />
          </Field>
          <Field label="Identificador">
            <Input
              value={form.slug}
              disabled={props.isEdit}
              onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
              placeholder="android"
            />
          </Field>
        </div>

        <p className="-mt-1 text-xs text-muted-foreground">
          El namespace agrupa etiquetas que responden a la misma pregunta. Una cosa etiquetada tiene
          que cruzar en <em>cada</em> namespace que declare, así que <code>audiencia:tecnico</code>{" "}
          y <code>dominio:android</code> juntos significan “sólo para agentes técnicos, y sólo en
          proyectos Android”. Déjalo vacío si la etiqueta no forma parte de ningún grupo.
          {props.isEdit && " No se puede cambiar: renombrarlo movería todo lo que ya la lleva."}
        </p>

        <Field label="Descripción">
          <Textarea
            rows={2}
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Para qué sirve, en una línea, para quien la vea en un selector."
          />
        </Field>
        <p className="-mt-2 text-xs text-muted-foreground">
          {form.description.trim().length}/{TAG_DESCRIPTION_MAX_LENGTH}
        </p>

        <Field label="Color">
          <div className="flex items-center gap-2">
            <Input
              value={form.color}
              onChange={(e) => setForm((prev) => ({ ...prev, color: e.target.value }))}
              placeholder="#3b82f6"
            />
            {/^#[0-9a-fA-F]{6}$/.test(form.color.trim()) && (
              <span
                aria-hidden
                className="size-6 shrink-0 rounded-md border border-border/60"
                style={{ backgroundColor: form.color.trim().toLowerCase() }}
              />
            )}
          </div>
        </Field>

        {error !== null && <p className="text-xs text-destructive">{error}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={props.onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={() => void save()} disabled={saving}>
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}

export function TagsSection() {
  const hasView = useAuthStore((state) => state.globalPermissions.has("t.view"));
  const hasEdit = useAuthStore((state) => state.globalPermissions.has("t.edit"));

  const [entries, setEntries] = useState<ReadonlyArray<AdminTagListEntry>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Tag | null>(null);

  const refresh = async (includeArchived: boolean) => {
    setLoading(true);
    setError(null);
    try {
      setEntries(await TagsClient.list({ includeArchived }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudieron cargar las etiquetas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasView) {
      setLoading(false);
      return;
    }
    void refresh(showArchived);
  }, [hasView, showArchived]);

  if (!hasView) {
    return (
      <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
        Necesitas el permiso <code>t.view</code> para ver las etiquetas.
      </div>
    );
  }

  const act = async (run: () => Promise<unknown>, failure: string) => {
    try {
      await run();
      await refresh(showArchived);
    } catch (err) {
      setError(err instanceof Error ? err.message : failure);
    }
  };

  const archive = (tag: Tag) => {
    // The wording is deliberate: nothing is deleted, and the chips come back if
    // the tag is reactivated. Saying "borrar" would promise the opposite.
    if (
      !window.confirm(
        `¿Retirar “${tag.label}”? Deja de aparecer en los selectores y deja de filtrar nada, ` +
          "pero se recuerda quién la llevaba por si la reactivas.",
      )
    ) {
      return;
    }
    void act(() => TagsClient.archive(tag.id), "No se pudo retirar la etiqueta.");
  };

  const live = entries.filter((entry) => entry.archivedAt === null).map((entry) => entry.tag);
  const archived = entries.filter((entry) => entry.archivedAt !== null).map((entry) => entry.tag);
  const groups = groupTagsByNamespace(live);

  return (
    <div className="flex flex-col gap-5">
      <div className="min-w-0">
        <h3 className="text-base font-semibold">Etiquetas</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Un vocabulario común para decidir a quién le sirve cada cosa. Una memoria o un comando
          etiquetado tiene que cruzar con las etiquetas del agente y del proyecto en <em>cada</em>{" "}
          namespace que declare; lo que no lleva etiquetas sigue estando disponible para todos.
        </p>
      </div>

      {error !== null && <p className="text-xs text-destructive">{error}</p>}

      {loading ? (
        <p className="text-xs text-muted-foreground">Cargando…</p>
      ) : groups.length === 0 ? (
        <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
          Todavía no hay etiquetas. Sin ninguna, todo sigue disponible para todos los agentes y
          proyectos — que es exactamente como funcionaba antes de que esta pantalla existiera.
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {groups.map((group) => (
            <div key={group.namespace} className="flex flex-col gap-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {group.namespace === "" ? "Sin namespace" : group.namespace}
              </h4>
              <div className="flex flex-col gap-2">
                {group.tags.map((tag) => (
                  <div
                    key={tag.id}
                    className="flex items-start justify-between gap-4 rounded-md border border-border/60 bg-card/40 p-3"
                  >
                    <div className="flex min-w-0 flex-col gap-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <TagChip tag={tag} />
                        <code className="text-xs text-muted-foreground">{formatTagRef(tag)}</code>
                      </div>
                      {tag.description !== null && (
                        <p className="text-xs text-muted-foreground">{tag.description}</p>
                      )}
                    </div>
                    {hasEdit && (
                      <div className="flex shrink-0 gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setEditing(tag)}>
                          Editar
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => archive(tag)}>
                          Retirar
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        {hasEdit && (
          <Button variant="outline" onClick={() => setCreating(true)}>
            Nueva etiqueta
          </Button>
        )}
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Ver retiradas
        </label>
      </div>

      {showArchived && !loading && (
        <div className="flex flex-col gap-2">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Retiradas
          </h4>
          {archived.length === 0 ? (
            <p className="text-xs text-muted-foreground">No hay etiquetas retiradas.</p>
          ) : (
            archived.map((tag) => (
              <div
                key={tag.id}
                className="flex items-center justify-between gap-4 rounded-md border border-dashed border-border/60 p-3"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <TagChip tag={tag} muted />
                  <code className="text-xs text-muted-foreground">{formatTagRef(tag)}</code>
                </div>
                {hasEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      void act(
                        () => TagsClient.restore(tag.id as TagId),
                        "No se pudo reactivar la etiqueta.",
                      )
                    }
                  >
                    Reactivar
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {creating && (
        <TagEditor
          initial={EMPTY_TAG_FORM}
          isEdit={false}
          onClose={() => setCreating(false)}
          onSave={async (form) => {
            await TagsClient.create(buildTagCreateRequest(form));
            await refresh(showArchived);
          }}
        />
      )}

      {editing !== null && (
        <TagEditor
          initial={tagToFormState(editing)}
          isEdit
          onClose={() => setEditing(null)}
          onSave={async (form) => {
            await TagsClient.update(editing.id, buildTagUpdateRequest(form));
            await refresh(showArchived);
          }}
        />
      )}
    </div>
  );
}
